import {
  Button,
  Container,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { ArrowBack } from "@material-ui/icons";
import { App } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";

import { useStateValue } from "../config/AppContext";
import Icons from "./Utilities/Icons";

const { bg_layer } = Icons;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    backArrow: {
      position: "absolute",
      top: 25,
      left: 15,
      cursor: "pointer",
      fill: "#ffffff",
      fontSize: 42,
    },
    container: {
      textAlign: "center",
      color: "#ffffff",
      height: "45vh",
      width: "100%",
      backgroundImage: `url(${bg_layer})`,
      "& h3": {
        marginBottom: theme.spacing(2),
        fontSize: 36,
        fontWidth: "bold",
      },
      "& p": {
        fontSize: 18,
        opacity: 0.8,
      },
    },
    title: {
      width: "100%",
      [theme.breakpoints.up("md")]: {
        width: "26%",
        margin: "auto",
      },
    },
    paper: {
      position: "absolute",
      top: "60%",
      width: "90%",
      maxWidth: 547,
      marginTop: theme.spacing(3),
      padding: 25,
      paddingLeft: 10,
      textAlign: "left",
      color: theme.palette.text.secondary,
      "& .MuiTextField-root": {
        margin: theme.spacing(1),
        backgroundColor: "#ffffff",
      },
    },
    actions: {
      marginTop: theme.spacing(1),
      marginLeft: 15,
      "& button": {
        margin: "0 auto",
        height: 53,
        fontSize: 17,
        textTransform: "none",
        backgroundColor: "#333333",
        color: "#ffffff",
        "&:hover": {
          backgroundColor: "#333333",
        },
      },
    },
  })
);

const LoginComponent: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { Methods } = useStateValue() as { Methods: App.Methods };

  const [email, setEmail] = React.useState<string | null>(null);
  const [password, setPassword] = React.useState<string | null>(null);

  const [toggle, setToggle] = React.useState(false);
  const [emailError, setEmailError] = React.useState<{
    state: boolean;
    text?: string;
  }>({ state: false });
  const [passwordError, setPasswordError] = React.useState<{
    state: boolean;
    text?: string;
  }>({ state: false });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    if (!toggle) {
      if (!password) return;
      return Methods.handleLogin({ email, password });
    } else if (toggle) {
      return Methods.recoverPass({ email });
    }
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;
    if (inputValue === "") {
      setEmailError({ state: true, text: t("Email is required") });
      return setEmail(inputValue);
    }
    const emailRegex = /^.+@.+$/;
    if (!emailRegex.test(inputValue)) {
      setEmailError({ state: true, text: t("Please enter valid email") });
      return setEmail(inputValue);
    }
    setEmail(inputValue);
    return setEmailError({ state: false });
  };

  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;
    if (inputValue === "") {
      setPasswordError({ state: true, text: t("Password is required") });
      return setPassword(inputValue);
    }
    setPassword(inputValue);
    return setPasswordError({ state: false });
  };

  React.useEffect(() => {
    const emailField = document.getElementById("email") as HTMLInputElement | null;
    const passwordField = document.getElementById("password") as HTMLInputElement | null;
    if (!emailField || !passwordField) return;
    if (emailField.value.length > 1 && !email) setEmail(emailField.value);
    if (passwordField.value.length > 1 && !password) setPassword(passwordField.value);
  }, [email, password]);

  return (
    <div id="Login">
      {toggle && <ArrowBack className={classes.backArrow} onClick={() => setToggle(false)} />}
      <Container className={classes.container}>
        <Typography variant="h3">
          {toggle ? t("Recover password") : t("Welcome To PT Flow")}
        </Typography>
        <Typography variant="body1" className={classes.title}>
          {toggle
            ? t("We will send a link for recovery to your email shortly")
            : t("PT Flow is a powerful yet simple way to collaborate with your clients")}
        </Typography>
        {/* <Typography variant='body1'>
          {toggle ? t('your email shortly') : t('to collaborate with your clients')}
        </Typography> */}
      </Container>
      <Container maxWidth="sm">
        <Paper className={classes.paper}>
          <form id="login-form" onSubmit={handleSubmit} autoComplete="off">
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  required
                  fullWidth
                  variant="outlined"
                  id="email"
                  name="email"
                  autoComplete="username"
                  label={t("Email")}
                  title=""
                  error={emailError.state}
                  helperText={emailError.text}
                  onChange={onEmailChange}
                  InputLabelProps={{
                    shrink: true,
                    style: { marginTop: 0, fontSize: 16 },
                  }}
                  onInvalid={() => {
                    const x = document.getElementById("email") as HTMLInputElement;
                    x?.setCustomValidity(`${t("This field is required")}`);
                  }}
                  onInput={() => {
                    const x = document.getElementById("email") as HTMLInputElement;
                    x?.setCustomValidity(``);
                  }}
                />
              </Grid>
              {!toggle && (
                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    required
                    fullWidth
                    variant="outlined"
                    id="password"
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    label={t("Password")}
                    title=""
                    error={passwordError.state}
                    helperText={passwordError.text}
                    onChange={onPasswordChange}
                    InputLabelProps={{
                      shrink: true,
                      style: { marginTop: 0, fontSize: 16 },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography
                            variant="caption"
                            color="primary"
                            onClick={() => setToggle(!toggle)}
                            style={{
                              cursor: "pointer",
                              color: "#3B5998",
                              fontSize: "14px",
                            }}
                          >
                            {t("Recover")}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    onInvalid={() => {
                      const x = document.getElementById("password") as HTMLInputElement;
                      x?.setCustomValidity(`${t("This field is required")}`);
                    }}
                    onInput={() => {
                      const x = document.getElementById("password") as HTMLInputElement;
                      x?.setCustomValidity(``);
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} className={classes.actions}>
                <Button fullWidth variant="contained" type="submit">
                  {!toggle ? t("Login") : t("Recover")}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </div>
  );
};

export default LoginComponent;
